import React, { useMemo, useTransition } from 'react'

import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { CenterText } from '../../../../../../../../../brunswick/components/atoms/CenterText'
import { trackEvent } from '../../../../../../../../../lib/analytics'
import { VisualFilterTile } from '../../../../../../../../../types/graphql'
import { useCollectionFilterQueryParamsContext } from '../../../../../../../../containers/CollectionFilterQueryParamsContext'
import { useCollectionFiltersContext } from '../../../containers/CollectionFiltersContext'

import { Body, Figcaption, ImageWrapper, Label, Image as StyledImage } from './styles'

interface Props extends VisualFilterTile {
  visibleTilesDesktop: number
  visibleTilesMobile: number
}

export const VisualFilterSectionTile: React.FC<Props> = ({
  ctaText,
  image,
  id,
  value,
  filter: filterFromField,
  filterSpec,
  visibleTilesDesktop,
  visibleTilesMobile,
}) => {
  const { updateFilterSelection, selectedFilters } = useCollectionFilterQueryParamsContext()
  const { filters } = useCollectionFiltersContext()
  const [isUpdateFiltersTransitionPending, startUpdateFiltersTransition] = useTransition()
  const isChecked = useMemo(() => {
    const includedInFilters = Object.values(selectedFilters).flat().includes(value)
    return isUpdateFiltersTransitionPending ? !includedInFilters : includedInFilters
  }, [isUpdateFiltersTransitionPending, selectedFilters, value])

  const filter = filterSpec?.name || filterFromField || ''

  const getVisualFilterType = useMemo(
    () => filters?.find(f => f.displayName.toLocaleLowerCase() === filter),
    [filters, filter]
  )

  const letterSpacing = useMemo(() => (isUppercase(ctaText) ? '0.15em' : '0.05em'), [ctaText])

  const isDisabled = useMemo(
    () => getVisualFilterType?.filterValues?.some(o => o.value === value && o.state === 'DISABLED'),
    [getVisualFilterType, value]
  )

  const toggleChecked = () => {
    if (isUpdateFiltersTransitionPending) return
    startUpdateFiltersTransition(() => {
      updateFilterSelection(filter, value, !isChecked)
    })
  }

  const selectedFilterValuesAsString = useMemo(() => {
    return Object.values(selectedFilters)
      .map(filterValues => filterValues.join(','))
      .join(',')
  }, [selectedFilters])

  return (
    <Body
      tileVariantDesktop={'circle'}
      tileVariantMobile={'circle'}
      isDisabled={!!isDisabled}
      isChecked={isChecked}
    >
      <Label
        htmlFor={`checkbox-${id}`}
        {...trackEvent({
          customAttributes: {
            'data-heap-rolling-filters': selectedFilterValuesAsString,
            'data-heap-visual-filter-value': value,
            'data-heap-filter-type': 'visual',
          },
          category: 'page-tile-section-tile',
          action: 'click',
          label: value,
        })}
      >
        <input
          disabled={!!isDisabled}
          checked={isChecked}
          type='checkbox'
          id={`checkbox-${id}`}
          style={{ opacity: '0', position: 'absolute', inset: '0' }}
          onChange={toggleChecked}
        />
        <ImageWrapper>
          <StyledImage
            alt={ctaText}
            src={image}
            loading='lazy'
            aspectRatios={{
              sm: 1,
              md: 1,
            }}
            widths={{
              unit: 'vw',
              sm: 100 / visibleTilesMobile,
              md: 100 / visibleTilesDesktop,
            }}
          />
        </ImageWrapper>
      </Label>

      {ctaText?.length > 0 ? (
        <Figcaption
          isUppercase={isUppercase(ctaText)}
          isDisabled={isDisabled || false}
          isChecked={isChecked}
          onClick={() => {
            if (!isDisabled) {
              toggleChecked()
            }
          }}
        >
          <CenterText {...{ letterSpacing }}>{ctaText}</CenterText>
        </Figcaption>
      ) : null}
    </Body>
  )
}
